/**
 * BucketPanel — Horizontal bar of category bucket buttons for GPU-based filtering.
 *
 * Positioned above the shape-switcher bar at the bottom of the viewport.
 * Each bucket button shows label + count. Selecting a bucket triggers a
 * GPU filter (desaturate/fade non-matching particles via FilterTexture).
 */

import React from 'react';

export interface Bucket {
    label: string;
    count: number;
    indices: number[];
}

interface BucketPanelProps {
    buckets: Bucket[];
    activeBucket: string | null;
    onSelect: (bucket: Bucket | null) => void;
    columnLabel?: string;
    /** render as a plain flex row inside a parent dock (no fixed positioning) — Wave bottom dock 2026-09-13 */
    inline?: boolean;
}

const BucketPanel: React.FC<BucketPanelProps> = ({ buckets, activeBucket, onSelect, columnLabel, inline }) => {
    if (buckets.length === 0) return null;

    return (
        <div style={inline ? {
            flex: '1 1 auto', minWidth: 0,
            display: 'flex', gap: '2px', padding: '4px 0', alignItems: 'center',
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        } : {
            position: 'fixed',
            bottom: '52px',
            left: 0,
            right: 0,
            zIndex: 9997,
            display: 'flex',
            gap: '2px',
            padding: '6px 8px',
            background: 'rgba(8,12,24,0.85)',
            backdropFilter: 'blur(8px)',
            borderTop: '1px solid rgba(59,130,246,0.1)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            alignItems: 'center',
        }}>
            {columnLabel && (
                <span style={{
                    flex: '0 0 auto',
                    fontSize: '9px',
                    fontFamily: 'monospace',
                    color: '#475569',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginRight: '4px',
                    whiteSpace: 'nowrap',
                }}>
                    {columnLabel}
                </span>
            )}

            {/* Clear button */}
            <button
                onClick={() => onSelect(null)}
                style={{
                    flex: '0 0 auto',
                    padding: '5px 8px',
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    fontWeight: activeBucket === null ? 700 : 400,
                    color: activeBucket === null ? '#f1f5f9' : '#64748b',
                    background: activeBucket === null ? 'rgba(100,116,139,0.3)' : 'rgba(30,41,59,0.5)',
                    border: `1px solid ${activeBucket === null ? 'rgba(100,116,139,0.5)' : 'rgba(59,130,246,0.1)'}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                }}
            >
                All
            </button>

            {/* Bucket buttons */}
            {buckets.map((bucket) => {
                const isActive = activeBucket === bucket.label;
                return (
                    <button
                        key={bucket.label}
                        onClick={() => onSelect(isActive ? null : bucket)}
                        style={{
                            flex: '0 0 auto',
                            padding: '5px 8px',
                            fontSize: '10px',
                            fontFamily: 'monospace',
                            fontWeight: isActive ? 700 : 400,
                            color: isActive ? '#f1f5f9' : '#94a3b8',
                            background: isActive
                                ? 'rgba(59,130,246,0.4)'
                                : 'rgba(30,41,59,0.5)',
                            border: `1px solid ${isActive ? 'rgba(59,130,246,0.6)' : 'rgba(59,130,246,0.1)'}`,
                            borderRadius: '4px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {bucket.label} <span style={{ opacity: 0.6 }}>({bucket.count})</span>
                    </button>
                );
            })}
        </div>
    );
};

export default BucketPanel;
